import { useState, useEffect, useCallback } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import axios from "axios";
import { 
  Search, MapPin, Stethoscope, Shield, Filter, 
  UserPlus, Send, MessageSquare, X, ChevronDown, Users
} from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Avatar, AvatarFallback, AvatarImage } from "../components/ui/avatar";
import { Badge } from "../components/ui/badge";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "../components/ui/dialog";
import { Sidebar } from "./Dashboard";
import { useAuth, API } from "../App";
import { toast } from "sonner";

const Directory = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const { getAuthHeaders } = useAuth();
  
  const [physicians, setPhysicians] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [specialty, setSpecialty] = useState("");
  const [state, setState] = useState("");
  const [specialties, setSpecialties] = useState([]);
  const [selectedPhysician, setSelectedPhysician] = useState(null);
  const [showReferralModal, setShowReferralModal] = useState(false);

  const fetchSpecialties = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/options/specialties`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setSpecialties(response.data.specialties);
    } catch (error) {
      toast.error("Unable to load specialties");
    }
  }, [getAuthHeaders]);

  useEffect(() => {
    fetchSpecialties();
  }, [fetchSpecialties]);

  const fetchDirectory = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (search) params.append("search", search);
      if (specialty) params.append("specialty", specialty);
      if (state) params.append("state", state);
      
      const response = await axios.get(`${API}/directory?${params}`, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      setPhysicians(response.data.physicians);
    } catch (error) {
      toast.error("Failed to load directory");
    } finally {
      setLoading(false);
    }
  }, [search, specialty, state, getAuthHeaders]);

  useEffect(() => {
    const debounce = setTimeout(fetchDirectory, 300);
    return () => clearTimeout(debounce);
  }, [fetchDirectory]);

  const US_STATES = [
    "AL", "AK", "AZ", "AR", "CA", "CO", "CT", "DE", "FL", "GA",
    "HI", "ID", "IL", "IN", "IA", "KS", "KY", "LA", "ME", "MD",
    "MA", "MI", "MN", "MS", "MO", "MT", "NE", "NV", "NH", "NJ",
    "NM", "NY", "NC", "ND", "OH", "OK", "OR", "PA", "RI", "SC",
    "SD", "TN", "TX", "UT", "VT", "VA", "WA", "WV", "WI", "WY"
  ];

  const handleStartReferral = (physician) => {
    setSelectedPhysician(physician);
    setShowReferralModal(true);
  };

  return (
    <div className="min-h-screen bg-slate-50 lg:pl-64">
      <Sidebar currentPath={location.pathname} />
      
      <main className="pt-20 lg:pt-8 p-4 lg:p-8" data-testid="directory-main">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="mb-8">
            <h1 className="text-2xl md:text-3xl font-bold text-slate-900 mb-2" style={{ fontFamily: 'Plus Jakarta Sans' }}>
              Physician Directory
            </h1>
            <p className="text-slate-600">Find and connect with trusted healthcare providers</p>
          </div>

          {/* Filters */}
          <div className="bg-white rounded-xl border border-slate-200 p-4 mb-6">
            <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
              <div className="relative md:col-span-2">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-slate-400" />
                <Input
                  placeholder="Search by name, specialty, or practice..."
                  value={search}
                  onChange={(e) => setSearch(e.target.value)}
                  className="pl-10"
                  data-testid="directory-search"
                />
              </div>
              
              <Select value={specialty || "all"} onValueChange={(v) => setSpecialty(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="specialty-filter">
                  <Stethoscope className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All Specialties" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Specialties</SelectItem>
                  {specialties.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>

              <Select value={state || "all"} onValueChange={(v) => setState(v === "all" ? "" : v)}>
                <SelectTrigger data-testid="state-filter">
                  <MapPin className="w-4 h-4 mr-2 text-slate-400" />
                  <SelectValue placeholder="All States" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All States</SelectItem>
                  {US_STATES.map((s) => (
                    <SelectItem key={s} value={s}>{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Results */}
          {loading ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {[1, 2, 3, 4, 5, 6].map((i) => (
                <div key={i} className="bg-white rounded-xl border border-slate-200 p-6 animate-pulse">
                  <div className="flex items-center gap-4 mb-4">
                    <div className="w-14 h-14 bg-slate-200 rounded-full"></div>
                    <div className="flex-1">
                      <div className="h-4 bg-slate-200 rounded w-3/4 mb-2"></div>
                      <div className="h-3 bg-slate-200 rounded w-1/2"></div>
                    </div>
                  </div>
                  <div className="h-3 bg-slate-200 rounded w-full mb-2"></div>
                  <div className="h-3 bg-slate-200 rounded w-2/3"></div>
                </div>
              ))}
            </div>
          ) : physicians.length > 0 ? (
            <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
              {physicians.map((physician, index) => (
                <motion.div
                  key={physician.user_id || physician.npi || index}
                  initial={{ opacity: 0, y: 20 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: index * 0.05 }}
                  className="bg-white rounded-xl border border-slate-200 p-6 hover:shadow-md hover:border-primary/50 transition-all duration-300"
                >
                  <div className="flex items-start gap-4 mb-4">
                    <Avatar className="w-14 h-14">
                      <AvatarImage src={physician.picture} />
                      <AvatarFallback className="bg-primary/10 text-primary text-lg">
                        {physician.name?.charAt(0) || 'P'}
                      </AvatarFallback>
                    </Avatar>
                    <div className="flex-1 min-w-0">
                      <h3 className="font-semibold text-slate-900 truncate">{physician.name}</h3>
                      <p className="text-primary font-medium">{physician.specialty || 'General'}</p>
                      {physician.practice_name && (
                        <p className="text-sm text-slate-500 truncate">{physician.practice_name}</p>
                      )}
                    </div>
                  </div>

                  <div className="space-y-2 mb-4">
                    {physician.city && physician.state && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <MapPin className="w-4 h-4 text-slate-400" />
                        {physician.city}, {physician.state}
                      </div>
                    )}
                    {physician.insurance_accepted?.length > 0 && (
                      <div className="flex items-center gap-2 text-sm text-slate-600">
                        <Shield className="w-4 h-4 text-slate-400" />
                        {physician.insurance_accepted.slice(0, 2).join(", ")}
                        {physician.insurance_accepted.length > 2 && ` +${physician.insurance_accepted.length - 2} more`}
                      </div>
                    )}
                  </div>

                  {physician.accepting_referrals && (
                    <Badge className="bg-emerald-50 text-emerald-700 border border-emerald-200 mb-4">
                      Accepting Referrals
                    </Badge>
                  )}
                  {physician.source === "npi" && !physician.accepting_referrals && (
                    <Badge className="bg-slate-50 text-slate-500 border border-slate-200 mb-4">
                      NPI Listed
                    </Badge>
                  )}

                  <div className="flex gap-2">
                    {physician.user_id ? (
                      <>
                        <Button 
                          size="sm" 
                          className="flex-1 bg-primary text-white hover:bg-primary/90"
                          onClick={() => handleStartReferral(physician)}
                          data-testid={`refer-${physician.user_id}`}
                        >
                          <Send className="w-4 h-4 mr-1" />
                          Refer
                        </Button>
                        <Button 
                          size="sm" 
                          variant="outline"
                          onClick={() => navigate(`/messages?to=${physician.user_id}`)}
                        >
                          <MessageSquare className="w-4 h-4" />
                        </Button>
                      </>
                    ) : (
                      <Button 
                        size="sm" 
                        variant="outline"
                        className="flex-1"
                        onClick={() => {
                          const name = physician.name?.split(' ').map(w => w.charAt(0).toUpperCase() + w.slice(1).toLowerCase()).join(' ');
                          const subject = encodeURIComponent("Join iConcierge Healthcare Marketing");
                          const body = encodeURIComponent(`Hi ${name},\n\nI'd like to invite you to join the iConcierge platform.\n\nClaim your profile: https://iconciergehm.com/browse\n`);
                          window.open(`mailto:?subject=${subject}&body=${body}`, '_blank');
                          toast.success("Opening email to invite provider");
                        }}
                      >
                        <Users className="w-4 h-4 mr-1" />
                        Invite to Join
                      </Button>
                    )}
                  </div>
                </motion.div>
              ))}
            </div>
          ) : (
            <div className="text-center py-16">
              <Search className="w-16 h-16 mx-auto mb-4 text-slate-300" />
              <h3 className="text-lg font-semibold text-slate-900 mb-2">No physicians found</h3>
              <p className="text-slate-600 mb-6">
                {search || specialty || state 
                  ? "Try adjusting your filters to see more results"
                  : "Be the first to complete your profile and appear in the directory"}
              </p>
              {(search || specialty || state) && (
                <Button 
                  variant="outline"
                  onClick={() => { setSearch(""); setSpecialty(""); setState(""); }}
                >
                  Clear Filters
                </Button>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Referral Modal */}
      <ReferralModal 
        open={showReferralModal}
        onClose={() => setShowReferralModal(false)}
        physician={selectedPhysician}
      />
    </div>
  );
};

// Referral Modal Component
const ReferralModal = ({ open, onClose, physician }) => {
  const { getAuthHeaders } = useAuth();
  const [loading, setLoading] = useState(false);
  const [formData, setFormData] = useState({
    patient_name: "",
    patient_phone: "",
    patient_email: "",
    reason: "",
    urgency: "routine",
    notes: ""
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!physician) return;
    
    setLoading(true);
    try {
      await axios.post(`${API}/referrals`, {
        ...formData,
        to_user_id: physician.user_id
      }, {
        headers: getAuthHeaders(),
        withCredentials: true
      });
      
      toast.success(`Referral sent to Dr. ${physician.name}`);
      onClose();
      setFormData({
        patient_name: "",
        patient_phone: "",
        patient_email: "",
        reason: "",
        urgency: "routine",
        notes: ""
      });
    } catch (error) {
      const detail = error.response?.data?.detail;
      const message = typeof detail === 'string' ? detail : detail?.message || "Failed to send referral";
      toast.error(message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-3">
            <Avatar className="w-10 h-10">
              <AvatarImage src={physician?.picture} />
              <AvatarFallback className="bg-primary/10 text-primary">
                {physician?.name?.charAt(0)}
              </AvatarFallback>
            </Avatar>
            <div>
              <p>Refer Patient to</p>
              <p className="font-normal text-primary">{physician?.name}</p>
            </div>
          </DialogTitle>
        </DialogHeader>

        <form onSubmit={handleSubmit} className="space-y-4 mt-4">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="text-sm font-medium text-slate-700">Patient Name *</label>
              <Input
                value={formData.patient_name}
                onChange={(e) => setFormData({...formData, patient_name: e.target.value})}
                placeholder="John Doe"
                required
                data-testid="referral-patient-name"
              />
            </div>
            <div>
              <label className="text-sm font-medium text-slate-700">Phone *</label>
              <Input
                value={formData.patient_phone}
                onChange={(e) => setFormData({...formData, patient_phone: e.target.value})}
                placeholder="(555) 123-4567"
                required
                data-testid="referral-patient-phone"
              />
            </div>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Patient Email</label>
            <Input
              type="email"
              value={formData.patient_email}
              onChange={(e) => setFormData({...formData, patient_email: e.target.value})}
              placeholder="patient@email.com"
              data-testid="referral-patient-email"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Reason for Referral *</label>
            <Input
              value={formData.reason}
              onChange={(e) => setFormData({...formData, reason: e.target.value})}
              placeholder="e.g., Chest pain evaluation"
              required
              data-testid="referral-reason"
            />
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Urgency</label>
            <Select value={formData.urgency} onValueChange={(v) => setFormData({...formData, urgency: v})}>
              <SelectTrigger data-testid="referral-urgency">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="routine">Routine</SelectItem>
                <SelectItem value="urgent">Urgent</SelectItem>
                <SelectItem value="emergency">Emergency</SelectItem>
              </SelectContent>
            </Select>
          </div>

          <div>
            <label className="text-sm font-medium text-slate-700">Additional Notes</label>
            <textarea
              value={formData.notes}
              onChange={(e) => setFormData({...formData, notes: e.target.value})}
              placeholder="Any additional information..."
              className="w-full min-h-[80px] px-3 py-2 border border-slate-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary"
              data-testid="referral-notes"
            />
          </div>

          <div className="flex gap-3 pt-2">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancel
            </Button>
            <Button 
              type="submit" 
              className="flex-1 bg-primary text-white hover:bg-primary/90"
              disabled={loading}
              data-testid="submit-referral-btn"
            >
              {loading ? "Sending..." : "Send Referral"}
            </Button>
          </div>
        </form>
      </DialogContent>
    </Dialog>
  );
};

export default Directory;
